import Phaser from 'phaser';
import { getAuth } from '../../store/auth.js';
import { getProfile } from '../../store/profile.js';
import { attachBottomNav, bottomNavTopY, preloadNavAssets } from '../view/AppNav.js';
import { BetaDiagnosticModal } from '../view/BetaDiagnosticModal.js';
import { mountPageHeader } from '../view/PageHeader.js';
import { ChangeEmailModal } from '../view/ChangeEmailModal.js';
import { DeleteAccountModal } from '../view/DeleteAccountModal.js';
import { ResetPasswordModal } from '../view/ResetPasswordModal.js';
import { Switch } from '../view/Switch.js';
import { updateEmailNotifications } from '../../store/profile.js';
import { SectionTabs } from '../view/SectionTabs.js';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../PhaserGame.js';

// Mirrors AboutScene + HowToScene patterns — same header band,
// `> SECTION` headers, `[+]` bullets, terminal voice. Single section:
//
//   - ACCOUNT — username + email, Change email / Reset password /
//               Delete account buttons. NOTIFICATIONS + STATS
//               sections were removed once the user-controllable
//               settings outgrew the placeholder copy; if either
//               returns, this is the spot.
//
// Page sits within one screen at 720×1280 with 1.5× text — scroll
// mechanism is wired (same pattern as About/HowTo) for resilience if
// content grows, but the typical visit shouldn't scroll.

const PAGE_BG_INT = 0x040713;

const SIDE_PADDING = 36;
const VIEWPORT_BOTTOM_INSET = 24;

const LINE_GAP_TIGHT = 6;
const LINE_GAP_LOOSE = 18;
const SECTION_GAP = 36;

// Text scale knob — same 1.5× as About / HowTo so the three terminal
// pages have identical body-type rhythm.
const TEXT_SCALE = 1.5;
function fs(base: number): string {
  return `${Math.round(base * TEXT_SCALE)}px`;
}

export class SettingsScene extends Phaser.Scene {
  private content!: Phaser.GameObjects.Container;
  private contentHeight = 0;
  // Y-coordinate of the bottom of the page-header logo. Set in
  // create() from mountPageHeader's return; used by the scroll mask
  // + drag-input gating so the logo (sitting in scene root) stays
  // pinned while content scrolls underneath.
  private contentTop = 0;
  private dragStart: { pointerY: number; contentY: number } | null = null;

  // Single-modal-at-a-time guards. Re-entrant taps while a modal is
  // up no-op until it closes.
  private deleteModal: DeleteAccountModal | null = null;
  private changeEmailModal: ChangeEmailModal | null = null;
  private resetPasswordModal: ResetPasswordModal | null = null;
  private betaDiagnosticModal: BetaDiagnosticModal | null = null;

  constructor() {
    super('Settings');
  }

  preload(): void {
    preloadNavAssets(this);
    // Distinct preload key per scene (vs Auth/About/HowTo reusing
    // the same asset path) so Phaser's cache doesn't collide.
    this.load.image('settingsLogo', '/assets/logo2.png');
  }

  init(): void {
    // Reset per-instance state — scene.restart() bypasses the
    // constructor so these MUST be re-initialized here.
    this.contentHeight = 0;
    this.dragStart = null;
    this.deleteModal = null;
  }

  create(): void {
    this.drawBackground();
    const { contentTop } = mountPageHeader({
      scene: this,
      logoAssetKey: 'settingsLogo',
      showBack: true,
    });
    this.contentTop = contentTop;
    this.content = this.add.container(0, contentTop);
    this.buildContent();
    this.applyScrollMask();
    this.installScrollInput();
    attachBottomNav(this, 'settings');
  }

  // --- Background ---

  private drawBackground(): void {
    const g = this.add.graphics();
    g.fillStyle(PAGE_BG_INT, 1);
    g.fillRect(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT);
  }

  // --- Scrollable content ---

  private buildContent(): void {
    let y = 0;

    // Section nav butts directly against the logo (the logo lives in
    // scene root via mountPageHeader; content starts flush below it).
    y = this.addSectionNav(y);
    y += SECTION_GAP;

    y = this.addAccountSection(y);
    y += SECTION_GAP;

    y = this.addBetaSection(y);
    y += SECTION_GAP;

    y = this.addSignOff(y);

    this.contentHeight = y;
  }

  // Shared 3-tab section nav across Settings / HowTo / About. The
  // active tab matches whichever scene we're in; tapping another tab
  // is a scene.start(). Mounted below the logo per the inbox redesign
  // (modern app pattern: logo → tabs → content).
  private addSectionNav(y: number): number {
    const tabs = new SectionTabs(this, {
      x: 0,
      y,
      width: DESIGN_WIDTH,
      items: [
        { id: 'howto', label: 'HOW TO PLAY' },
        { id: 'settings', label: 'SETTINGS' },
        { id: 'about', label: 'INFO' },
      ],
      activeIndex: 1,
      onSelect: (id) => {
        // SectionTabs jumps within the Settings/HowTo/About group are
        // INTRA-group navigation, not history-pushing transitions.
        // The Back button exits the whole group at once — users
        // shouldn't have to back-tap through each tab they sampled.
        // Plain scene.start() instead of navigateTo() so the history
        // stack still points at whatever scene opened the group.
        if (id === 'howto') this.scene.start('HowTo');
        else if (id === 'about') this.scene.start('About');
        // 'settings' is current scene — handler is a no-op.
      },
    });
    this.content.add(tabs.root);
    return y + tabs.height;
  }

  /**
   * Account section: username + email + Change email / Reset
   * password / Delete account buttons. Logged-out visitors get a
   * single nudge to sign in from Home — the section is the only
   * thing on the page now, so an unauthenticated Settings visit is
   * intentionally a near-empty page.
   */
  private addAccountSection(y: number): number {
    y = this.addSectionHeader(y, 'ACCOUNT');

    const auth = getAuth();
    const profile = getProfile();

    if (!auth.user) {
      return this.addBulletList(
        y,
        ['Sign in from the home screen to manage your account.'],
        '[+]'
      );
    }

    const username =
      profile.kind === 'exists' ? profile.profile.username : '(setting up…)';
    const email = auth.user.email ?? '(none)';

    y = this.addBulletList(
      y,
      [`Username: ${username}`, `Email: ${email}`],
      '[+]'
    );
    y += LINE_GAP_LOOSE;

    // Email-notifications toggle. Reads current state from the
    // cached profile (default true if the row predates 0009). On
    // tap, optimistically flips the cache, fires the update; on
    // failure, reverts the visual. Mandatory emails (deletion
    // receipt, Supabase auth confirmations) bypass this flag.
    y = this.addEmailNotificationsRow(
      y,
      profile.kind === 'exists' ? profile.profile.emailNotifications : true
    );
    y += LINE_GAP_LOOSE;

    // Cyan "Change email" button — opens a small modal that calls
    // supabase.auth.updateUser({ email }). The confirmation flow
    // (link to new address, "your email was changed" to old
    // address) is handled by the already-branded Supabase
    // templates.
    y = this.addChangeEmailButton(y);
    y += LINE_GAP_LOOSE;

    // Cyan "Reset password" button — deliberately email-only (no
    // inline new-password field) so a momentary device-takeover
    // can't escalate to a permanent account takeover. The link
    // lands in ResetPasswordScene where the new password is set.
    y = this.addResetPasswordButton(y);
    y += LINE_GAP_LOOSE;

    // Inline delete button — interactive Rectangle + Text added to
    // the scrollable content container (rather than via Button at
    // scene root, which wouldn't scroll).
    y = this.addDeleteButton(y);

    return y;
  }

  /**
   * Email-notifications toggle row. Label on the left, slider on the
   * right, status text under the label. Optimistic UI: tap flips the
   * switch immediately and fires the API call; failure reverts.
   * Disabled state during the in-flight request prevents tap-spam.
   */
  private addEmailNotificationsRow(y: number, initial: boolean): number {
    const rowHeight = 64;
    const top = y;
    const labelX = SIDE_PADDING;
    const switchX = DESIGN_WIDTH - SIDE_PADDING - 50;
    const centerY = top + rowHeight / 2;

    const label = this.add
      .text(labelX, centerY, 'Email notifications', {
        fontFamily: FONT_HEADER,
        fontSize: fs(15),
        color: COLORS.textPrimary,
        fontStyle: '700',
      })
      .setLetterSpacing(1)
      .setOrigin(0, 0.5);
    this.content.add(label);

    // Status line tracks the switch state. Updated alongside the
    // switch on every tap so the user gets text confirmation of
    // the new state.
    const status = this.add
      .text(labelX, centerY + 22, initial ? 'ON' : 'OFF', {
        fontFamily: FONT_MONO,
        fontSize: fs(12),
        color: initial ? COLORS.accentCyanHex : COLORS.textMuted,
      })
      .setLetterSpacing(1)
      .setOrigin(0, 0.5);
    this.content.add(status);

    const toggle = new Switch({
      scene: this,
      x: switchX,
      y: centerY,
      initialValue: initial,
      onChange: (next) => {
        // Immediate visual response — status text + cached profile
        // already updated optimistically. Disable taps until the
        // server confirms.
        status.setText(next ? 'ON' : 'OFF');
        status.setColor(next ? COLORS.accentCyanHex : COLORS.textMuted);
        toggle.setEnabled(false);

        void updateEmailNotifications(next).then((result) => {
          if (!result.ok) {
            // Revert. setValue(false) won't fire onChange, so we
            // also manually restore the status text + cache.
            toggle.setValue(!next, true);
            status.setText(!next ? 'ON' : 'OFF');
            status.setColor(
              !next ? COLORS.accentCyanHex : COLORS.textMuted
            );
            console.warn('email-notifications toggle failed', result.error);
          }
          toggle.setEnabled(true);
        });
      },
    });
    this.content.add(toggle.root);

    return top + rowHeight;
  }

  private addChangeEmailButton(y: number): number {
    const cx = DESIGN_WIDTH / 2;
    const w = 320;
    const h = 64;
    const top = y;

    const bg = this.add
      .rectangle(cx, top + h / 2, w, h, COLORS.bgDeepInt)
      .setStrokeStyle(2, COLORS.accentCyanInt, 0.85)
      .setInteractive({ useHandCursor: true });

    const label = this.add
      .text(cx, top + h / 2, 'Change email', {
        fontFamily: FONT_HEADER,
        fontSize: fs(16),
        color: COLORS.accentCyanHex,
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5);

    bg.on('pointerup', () => this.handleChangeEmailTap());
    bg.on('pointerover', () =>
      bg.setStrokeStyle(2, COLORS.accentCyanInt, 1)
    );
    bg.on('pointerout', () =>
      bg.setStrokeStyle(2, COLORS.accentCyanInt, 0.85)
    );

    this.content.add(bg);
    this.content.add(label);

    return top + h;
  }

  private handleChangeEmailTap(): void {
    if (this.changeEmailModal) return;
    this.changeEmailModal = new ChangeEmailModal(this, {
      onCancel: () => {
        this.changeEmailModal = null;
      },
      onSuccess: () => {
        this.changeEmailModal = null;
      },
    });
  }

  private addResetPasswordButton(y: number): number {
    const cx = DESIGN_WIDTH / 2;
    const w = 320;
    const h = 64;
    const top = y;

    const bg = this.add
      .rectangle(cx, top + h / 2, w, h, COLORS.bgDeepInt)
      .setStrokeStyle(2, COLORS.accentCyanInt, 0.85)
      .setInteractive({ useHandCursor: true });

    const label = this.add
      .text(cx, top + h / 2, 'Reset password', {
        fontFamily: FONT_HEADER,
        fontSize: fs(16),
        color: COLORS.accentCyanHex,
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5);

    bg.on('pointerup', () => this.handleResetPasswordTap());
    bg.on('pointerover', () =>
      bg.setStrokeStyle(2, COLORS.accentCyanInt, 1)
    );
    bg.on('pointerout', () =>
      bg.setStrokeStyle(2, COLORS.accentCyanInt, 0.85)
    );

    this.content.add(bg);
    this.content.add(label);

    return top + h;
  }

  private handleResetPasswordTap(): void {
    if (this.resetPasswordModal) return;
    this.resetPasswordModal = new ResetPasswordModal(this, {
      onCancel: () => {
        this.resetPasswordModal = null;
      },
      onSuccess: () => {
        this.resetPasswordModal = null;
      },
    });
  }

  private addDeleteButton(y: number): number {
    // Red destructive styling — visually distinct from the cyan
    // tappables elsewhere in the page. Border + text both red;
    // hover bumps stroke alpha but keeps the color. The
    // accentRedInt is the same red used by the modal title and
    // panel border so the visual chord reads consistently.
    const cx = DESIGN_WIDTH / 2;
    const w = 320;
    const h = 64;
    const top = y;

    const bg = this.add
      .rectangle(cx, top + h / 2, w, h, COLORS.bgDeepInt)
      .setStrokeStyle(2, COLORS.accentRedInt, 0.85)
      .setInteractive({ useHandCursor: true });

    const label = this.add
      .text(cx, top + h / 2, 'Delete account', {
        fontFamily: FONT_HEADER,
        fontSize: fs(16),
        color: COLORS.accentRedHex,
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5);

    bg.on('pointerup', () => this.handleDeleteTap());
    bg.on('pointerover', () =>
      bg.setStrokeStyle(2, COLORS.accentRedInt, 1)
    );
    bg.on('pointerout', () =>
      bg.setStrokeStyle(2, COLORS.accentRedInt, 0.85)
    );

    this.content.add(bg);
    this.content.add(label);

    return top + h;
  }

  /**
   * Opens DeleteAccountModal. Guards re-entrant taps so a second
   * tap while the modal is mid-flight doesn't open a second
   * instance. Success → route to Home; cancel → just dismiss.
   */
  private handleDeleteTap(): void {
    if (this.deleteModal) return;
    this.deleteModal = new DeleteAccountModal(this, {
      onCancel: () => {
        this.deleteModal = null;
      },
      onSuccess: () => {
        this.deleteModal = null;
        this.scene.start('Home');
      },
    });
  }

  /**
   * Beta diagnostic section. Single-purpose button that opens the
   * BetaDiagnosticModal — a screenshot-friendly snapshot of app +
   * auth + network + data state so testers can attach a photo (or
   * paste the JSON) to bug reports without opening DevTools.
   *
   * Lives in Settings (visible to everyone) for the beta period.
   * Will move behind a `?debug=1` URL gate (or get removed) once the
   * beta closes.
   */
  private addBetaSection(y: number): number {
    y = this.addSectionHeader(y, 'BETA');
    y = this.addBulletList(
      y,
      ['Snapshot of app state for bug reports.'],
      '[+]'
    );
    y += LINE_GAP_LOOSE;
    y = this.addBetaDiagnosticButton(y);
    return y;
  }

  private addBetaDiagnosticButton(y: number): number {
    const cx = DESIGN_WIDTH / 2;
    const w = 320;
    const h = 64;
    const top = y;

    const bg = this.add
      .rectangle(cx, top + h / 2, w, h, COLORS.bgDeepInt)
      .setStrokeStyle(2, COLORS.accentCyanInt, 0.85)
      .setInteractive({ useHandCursor: true });

    const label = this.add
      .text(cx, top + h / 2, 'Run diagnostic', {
        fontFamily: FONT_HEADER,
        fontSize: fs(16),
        color: COLORS.accentCyanHex,
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5);

    bg.on('pointerup', () => this.handleBetaDiagnosticTap());
    bg.on('pointerover', () =>
      bg.setStrokeStyle(2, COLORS.accentCyanInt, 1)
    );
    bg.on('pointerout', () =>
      bg.setStrokeStyle(2, COLORS.accentCyanInt, 0.85)
    );

    this.content.add(bg);
    this.content.add(label);

    return top + h;
  }

  private handleBetaDiagnosticTap(): void {
    if (this.betaDiagnosticModal) return;
    this.betaDiagnosticModal = new BetaDiagnosticModal(this, {
      onClose: () => {
        this.betaDiagnosticModal = null;
      },
    });
  }

  private addSignOff(y: number): number {
    const cx = DESIGN_WIDTH / 2;

    const rule = this.add
      .text(cx, y, '─'.repeat(38), {
        fontFamily: FONT_MONO,
        fontSize: fs(14),
        color: COLORS.textMuted,
      })
      .setOrigin(0.5, 0);
    this.content.add(rule);
    y += rule.height + LINE_GAP_TIGHT;

    const signoff = this.add
      .text(cx, y, '// END OF SETTINGS', {
        fontFamily: FONT_MONO,
        fontSize: fs(13),
        color: COLORS.textMuted,
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5, 0);
    this.content.add(signoff);
    return y + signoff.height;
  }

  // --- Section helpers ---

  private addSectionHeader(y: number, title: string): number {
    const header = this.add
      .text(SIDE_PADDING, y, `> ${title}`, {
        fontFamily: FONT_MONO,
        fontSize: fs(17),
        color: COLORS.accentCyanHex,
        fontStyle: '700',
      })
      .setLetterSpacing(1)
      .setOrigin(0, 0);
    this.content.add(header);
    y += header.height + 4;

    const rule = this.add
      .text(SIDE_PADDING, y, '─'.repeat(34), {
        fontFamily: FONT_MONO,
        fontSize: fs(13),
        color: intToHex(COLORS.borderSubtleInt),
      })
      .setOrigin(0, 0);
    this.content.add(rule);
    return y + rule.height + LINE_GAP_LOOSE;
  }

  private addBulletList(
    y: number,
    items: readonly string[],
    marker: string,
    indent: number = SIDE_PADDING
  ): number {
    const wrapWidth = DESIGN_WIDTH - SIDE_PADDING - indent;
    for (const item of items) {
      const line = this.add
        .text(indent, y, `${marker} ${item}`, {
          fontFamily: FONT_MONO,
          fontSize: fs(14),
          color: COLORS.textPrimary,
          wordWrap: { width: wrapWidth },
        })
        .setLineSpacing(2)
        .setOrigin(0, 0);
      this.content.add(line);
      y += line.height + LINE_GAP_TIGHT;
    }
    return y;
  }

  // --- Scroll input ---

  private applyScrollMask(): void {
    // Scroll-visible area starts below the page-header logo and ends
    // above the bottom NavBar so content doesn't bleed under either.
    const maskBottom = bottomNavTopY();
    const shape = this.make.graphics({});
    shape.fillStyle(0xffffff, 1);
    shape.fillRect(
      0,
      this.contentTop,
      DESIGN_WIDTH,
      maskBottom - this.contentTop
    );
    const mask = shape.createGeometryMask();
    this.content.setMask(mask);
  }

  private installScrollInput(): void {
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      // Don't initiate drag from inside the page header (logo + Back
      // overlay) or the bottom nav strip — those areas own their
      // own taps.
      if (p.y < this.contentTop) return;
      if (p.y > bottomNavTopY()) return;
      this.dragStart = { pointerY: p.y, contentY: this.content.y };
    });
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!p.isDown || !this.dragStart) return;
      const dy = p.y - this.dragStart.pointerY;
      this.content.y = this.clampScroll(this.dragStart.contentY + dy);
    });
    this.input.on('pointerup', () => {
      this.dragStart = null;
    });
    this.input.on('pointerupoutside', () => {
      this.dragStart = null;
    });

    this.input.on(
      'wheel',
      (
        _p: Phaser.Input.Pointer,
        _go: Phaser.GameObjects.GameObject[],
        _dx: number,
        deltaY: number
      ) => {
        this.content.y = this.clampScroll(this.content.y - deltaY);
      }
    );
  }

  private clampScroll(rawY: number): number {
    // Bottom-of-viewport is now the top edge of the AppNav, not
    // the canvas bottom — content scrolled below the nav would be
    // hidden behind it.
    const viewportBottom = bottomNavTopY() - VIEWPORT_BOTTOM_INSET;
    const minY = Math.min(this.contentTop, viewportBottom - this.contentHeight);
    const maxY = this.contentTop;
    if (rawY > maxY) return maxY;
    if (rawY < minY) return minY;
    return rawY;
  }
}

function intToHex(int: number): string {
  return `#${int.toString(16).padStart(6, '0')}`;
}
